import { supabase } from './supabase';

export type SiteAnalyticsSummary = {
  anonymousVisitors: number;
  knownVisitors: number;
  anonymousPageViews: number;
  knownPageViews: number;
  totalPageViews: number;
};

export type SiteAnalyticsVisitor = {
  subscriberId: string | null;
  email: string | null;
  name: string | null;
  pageViews: number;
  paths: string[];
  firstSeen: string;
  lastSeen: string;
  inCrm: boolean;
};

export type SiteAnalyticsAnonymousVisitor = {
  distinctId: string;
  pageViews: number;
  paths: string[];
  firstSeen: string;
  lastSeen: string;
};

export type SiteAnalyticsTrendPoint = {
  periodStart: string;
  anonymousViews: number;
  knownViews: number;
};

export type SiteAnalyticsTrendBucketMode = '5min' | '30min' | 'hourly' | '6hour' | 'daily';

export type SiteAnalyticsResult = {
  periodHours: number;
  summary: SiteAnalyticsSummary;
  visitors: SiteAnalyticsVisitor[];
  anonymousVisitors: SiteAnalyticsAnonymousVisitor[];
  posthogProjectUrl: string | null;
  trend?: SiteAnalyticsTrendPoint[];
  trendPeriodHours?: number;
  trendBucketMode?: SiteAnalyticsTrendBucketMode;
};

export type SiteAnalyticsChartPeriodHours = 1 | 2 | 6 | 12 | 48 | 168 | 360 | 720 | 1440;

export const SITE_ANALYTICS_CHART_PERIOD_OPTIONS: Array<{
  hours: SiteAnalyticsChartPeriodHours;
  label: string;
}> = [
  { hours: 1, label: 'Last 1 hour' },
  { hours: 2, label: 'Last 2 hours' },
  { hours: 6, label: 'Last 6 hours' },
  { hours: 12, label: 'Last 12 hours' },
  { hours: 48, label: 'Last 2 days' },
  { hours: 168, label: 'Last 7 days' },
  { hours: 360, label: 'Last 15 days' },
  { hours: 720, label: 'Last 30 days' },
  { hours: 1440, label: 'Last 60 days' },
];

export type LoadSiteAnalyticsOptions = {
  /** @deprecated Trend always matches periodHours. */
  trendPeriodHours?: SiteAnalyticsChartPeriodHours;
};

async function edgeFunctionErrorMessage(
  error: { message?: string; context?: unknown },
  data: { error?: string } | null | undefined,
  fallback: string,
): Promise<string> {
  const ctx = error.context;
  if (ctx && typeof (ctx as Response).json === 'function') {
    try {
      const parsed = (await (ctx as Response).clone().json()) as { error?: string };
      if (parsed?.error) return parsed.error;
    } catch {
      try {
        const text = await (ctx as Response).clone().text();
        if (text.trim()) return text;
      } catch {
        /* ignore */
      }
    }
  }
  return data?.error || error.message || fallback;
}

export async function loadSiteAnalytics(
  periodHours: SiteAnalyticsChartPeriodHours,
  _options: LoadSiteAnalyticsOptions = {},
): Promise<SiteAnalyticsResult> {
  const { data, error } = await supabase.functions.invoke<SiteAnalyticsResult & { error?: string }>(
    'posthog-site-activity',
    {
      body: { periodHours, trendPeriodHours: periodHours },
    },
  );

  if (error) {
    throw new Error(
      await edgeFunctionErrorMessage(error, data, 'Failed to load site analytics'),
    );
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  if (!data?.summary || !data.visitors) {
    throw new Error('Unexpected response from posthog-site-activity');
  }

  return {
    ...data,
    anonymousVisitors: data.anonymousVisitors ?? [],
  };
}

export function formatAnonymousDistinctId(distinctId: string): string {
  const trimmed = distinctId.trim();
  if (trimmed.length <= 12) return trimmed;
  return `${trimmed.slice(0, 8)}…${trimmed.slice(-4)}`;
}

export function formatSiteAnalyticsWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatSiteAnalyticsRelativeWhen(iso: string): string | null {
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;

    const diffSec = Math.round((Date.now() - then) / 1000);
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

    const absSec = Math.abs(diffSec);
    if (absSec < 60) return rtf.format(-diffSec, 'second');

    const diffMin = Math.round(diffSec / 60);
    if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, 'minute');

    const diffHr = Math.round(diffMin / 60);
    if (Math.abs(diffHr) < 24) return rtf.format(-diffHr, 'hour');

    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffDay) < 30) return rtf.format(-diffDay, 'day');

    const diffMonth = Math.round(diffDay / 30);
    if (Math.abs(diffMonth) < 12) return rtf.format(-diffMonth, 'month');

    return rtf.format(-Math.round(diffDay / 365), 'year');
  } catch {
    return null;
  }
}

export function formatPathsPreview(paths: string[], max = 3): string {
  if (paths.length === 0) return '—';
  const head = paths.slice(0, max).join(', ');
  if (paths.length <= max) return head;
  return `${head} +${paths.length - max} more`;
}
